import type { GitChangeSetResponse, GitFileInfo, GitSummaryResponse, UncommittedChangesStats } from "../nestAPIs/git"

const TEST_FILE_RULES = [
    { pattern: "**/*.test.{js,jsx,ts,tsx,mjs,cjs,mts,cts}", matcher: /(?:^|\/)[^/]+\.test\.(?:js|jsx|ts|tsx|mjs|cjs|mts|cts)$/i },
    { pattern: "**/*.spec.{js,jsx,ts,tsx,mjs,cjs,mts,cts}", matcher: /(?:^|\/)[^/]+\.spec\.(?:js|jsx|ts|tsx|mjs|cjs|mts|cts)$/i },
    { pattern: "**/*.{test,spec}", matcher: /(?:^|\/)[^/]+\.(?:test|spec)$/i },
    { pattern: "**/*_test.go", matcher: /(?:^|\/)[^/]+_test\.go$/i },
    { pattern: "**/{test_*,*_test}.py", matcher: /(?:^|\/)(?:test_[^/]+|[^/]+_test)\.py$/i },
    { pattern: "**/*_spec.rb", matcher: /(?:^|\/)[^/]+_spec\.rb$/i },
    {
        pattern: "**/*{Test,Tests,Spec,Specs}.{java,kt,kts,scala,groovy}",
        matcher: /(?:^|\/)[^/]+(?:Test|Tests|Spec|Specs)\.(?:java|kt|kts|scala|groovy)$/,
    },
    { pattern: "**/*{Test,Tests,Spec,Specs}.{cs,fs,vb,php,swift}", matcher: /(?:^|\/)[^/]+(?:Test|Tests|Spec|Specs)\.(?:cs|fs|vb|php|swift)$/ },
    {
        pattern: "**/*_{test,tests,spec}.{c,cc,cpp,cxx,h,hh,hpp,hxx,rs}",
        matcher: /(?:^|\/)[^/]+_(?:test|tests|spec)\.(?:c|cc|cpp|cxx|h|hh|hpp|hxx|rs)$/i,
    },
    { pattern: "**/*_test.exs", matcher: /(?:^|\/)[^/]+_test\.exs$/i },
    { pattern: "**/{test_*,*_test}.sh", matcher: /(?:^|\/)(?:test_[^/]+|[^/]+_test)\.sh$/i },
    { pattern: "**/*.bats", matcher: /(?:^|\/)[^/]+\.bats$/i },
    { pattern: "**/*.snap", matcher: /(?:^|\/)[^/]+\.snap$/i },
    {
        pattern: "**/{test,tests,__tests__,spec,specs,e2e,cypress,__snapshots__}/**",
        matcher: /(?:^|\/)(?:test|tests|__tests__|spec|specs|e2e|cypress|__snapshots__)(?:\/|$)/i,
    },
    {
        pattern: "**/src/{test,androidTest,commonTest,iosTest,jvmTest,testFixtures}/**",
        matcher: /(?:^|\/)src\/(?:test|androidTest|commonTest|iosTest|jvmTest|testFixtures)(?:\/|$)/,
    },
] as const

export const TEST_FILE_PATTERNS = TEST_FILE_RULES.map((rule) => rule.pattern)

export function isTestFilePath(path: string): boolean {
    const normalizedPath = path.replaceAll("\\", "/")
    return TEST_FILE_RULES.some((rule) => rule.matcher.test(normalizedPath))
}

function summarizeFiles(files: GitFileInfo[]): UncommittedChangesStats {
    let binaryFiles = 0
    let insertions = 0
    let deletions = 0
    let unknownFiles = 0

    for (const file of files) {
        if (file.binary) {
            binaryFiles++
        } else if (file.lineStats) {
            insertions += file.lineStats.insertions
            deletions += file.lineStats.deletions
        } else {
            unknownFiles++
        }
    }

    return { filesChanged: files.length, binaryFiles, insertions, deletions, unknownFiles }
}

function filterChangeSet(changeSet: GitChangeSetResponse): GitChangeSetResponse {
    const files = changeSet.files.filter((file) => !isTestFilePath(file.path))
    if (files.length === changeSet.files.length) return changeSet
    return { files, stats: summarizeFiles(files) }
}

function totalStats(changeSets: GitChangeSetResponse[]): UncommittedChangesStats {
    const binaryByPath = new Map<string, boolean>()
    let insertions = 0
    let deletions = 0
    let unknownFiles = 0

    for (const changeSet of changeSets) {
        insertions += changeSet.stats.insertions
        deletions += changeSet.stats.deletions
        unknownFiles += changeSet.stats.unknownFiles ?? 0
        for (const file of changeSet.files) {
            binaryByPath.set(file.path, (binaryByPath.get(file.path) ?? false) || file.binary)
        }
    }

    return {
        filesChanged: binaryByPath.size,
        binaryFiles: [...binaryByPath.values()].filter(Boolean).length,
        insertions,
        deletions,
        unknownFiles,
    }
}

export function filterGitSummary(summary: GitSummaryResponse, excludeTestFiles: boolean): GitSummaryResponse {
    if (!excludeTestFiles) return summary

    const allFiles = [...summary.staged.files, ...summary.unstaged.files, ...summary.untracked.files]
    if (!allFiles.some((file) => isTestFilePath(file.path))) return summary

    const staged = filterChangeSet(summary.staged)
    const unstaged = filterChangeSet(summary.unstaged)
    const untracked = filterChangeSet(summary.untracked)
    const total = totalStats([staged, unstaged, untracked])

    return {
        ...summary,
        hasChanges: total.filesChanged > 0,
        staged,
        unstaged,
        untracked,
        total,
    }
}
